import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  LineChart,
  Line,
} from 'recharts';
import {
  Download,
  Filter,
  Map,
  PieChart as PieChartIcon,
  LineChart as LineChartIcon,
  Activity,
  Users,
  Scale,
  Heart,
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { ro } from 'date-fns/locale';
import { DateRange } from 'react-day-picker';
import { api } from '@/services/api';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface HospitalOption {
  id: number;
  name: string;
}

interface HospitalStats {
  total_patients?: number;
  patients_with_diagnoses?: number;
  total_diagnoses?: number;
  birth_weight_stats?: { avg_birth_weight?: number | null };
  gestational_age_stats?: { avg_gestational_age?: number | null };
  monthly_trend?: Array<{ month: string; count: number }>;
}

interface ChdDistributionItem {
  code: string;
  description?: string;
  count: number;
  percentage?: number;
}

interface SeverityStatsItem {
  level_name: string;
  count: number;
  percentage?: number;
}

interface DemographicsData {
  geographic_distribution?: Array<{ judet: string; count: number }>;
  age_distribution?: Array<{ age_group: string; count: number }>;
}

interface MaternalRiskData {
  total_with_maternal_data?: number;
  teen_mothers?: number;
  advanced_age_mothers?: number;
  smoking_count?: number;
  alcohol_count?: number;
  with_medications?: number;
  with_complications?: number;
}

interface BirthWeightAnalysis {
  categories?: Array<{ weight_category: string; count: number; percentage?: number }>;
  by_gestational_age?: Array<{ gestational_age: number; avg_birth_weight: number; count: number }>;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatPercentage = (value?: number | string | null) => {
  const numeric = toNumber(value);
  if (numeric === null) return '0%';
  return `${numeric.toFixed(1)}%`;
};

export const SystemAnalytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfMonth(subMonths(new Date(), 5)),
    to: endOfMonth(new Date()),
  });
  const [selectedHospital, setSelectedHospital] = useState<string>('all');
  const [hospitals, setHospitals] = useState<HospitalOption[]>([]);
  const [analyticsData, setAnalyticsData] = useState<{
    hospitalStats?: HospitalStats | null;
    chdDistribution?: ChdDistributionItem[];
    severityStats?: SeverityStatsItem[];
    demographics?: DemographicsData | null;
    maternalStats?: MaternalRiskData | null;
    birthWeight?: BirthWeightAnalysis | null;
  }>({});

  const loadHospitals = useCallback(async () => {
    try {
      const response = await api.get<Array<{ id: number; name: string }>>('/api/admin/hospitals');
      if (response.success && Array.isArray(response.data)) {
        setHospitals(response.data.map((h) => ({ id: h.id, name: h.name })));
      }
    } catch (err) {
      console.error('Nu s-a putut încărca lista de spitale:', err);
    }
  }, []);

  useEffect(() => {
    loadHospitals();
  }, [loadHospitals]);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string> = {};
      if (selectedHospital !== 'all') {
        params.hospital_id = selectedHospital;
      }
      if (dateRange?.from) {
        params.start_date = format(dateRange.from, 'yyyy-MM-dd');
      }
      if (dateRange?.to) {
        params.end_date = format(dateRange.to, 'yyyy-MM-dd');
      }

      const fallback = { success: false, data: null } as const;

      const [
        hospitalStatsRes,
        chdRes,
        severityRes,
        demographicsRes,
        maternalRes,
        birthWeightRes,
      ] = await Promise.all([
        api.get('/api/reports/hospital', { params }).catch(() => fallback),
        api.get('/api/reports/chd-distribution', { params }).catch(() => fallback),
        api.get('/api/reports/severity-statistics', { params }).catch(() => fallback),
        api.get('/api/reports/patient-demographics', { params }).catch(() => fallback),
        api.get('/api/reports/maternal-risk-factors', { params }).catch(() => fallback),
        api.get('/api/reports/birth-weight-analysis', { params }).catch(() => fallback),
      ]);

      setAnalyticsData({
        hospitalStats: hospitalStatsRes.success ? (hospitalStatsRes.data as HospitalStats) : null,
        chdDistribution: chdRes.success ? (chdRes.data as ChdDistributionItem[]) : [],
        severityStats: severityRes.success ? (severityRes.data as SeverityStatsItem[]) : [],
        demographics: demographicsRes.success ? (demographicsRes.data as DemographicsData) : null,
        maternalStats: maternalRes.success ? (maternalRes.data as MaternalRiskData) : null,
        birthWeight: birthWeightRes.success ? (birthWeightRes.data as BirthWeightAnalysis) : null,
      });
    } catch (err: any) {
      console.error('Eroare în Analize sistem:', err);
      setError(err?.response?.data?.error || 'Nu s-au putut încărca datele analitice');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedHospital]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const monthlyTrendData = useMemo(() => {
    const trend = analyticsData.hospitalStats?.monthly_trend || [];
    return trend
      .slice()
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((item) => ({
        month: format(new Date(`${item.month}-01`), 'MMM yyyy', { locale: ro }),
        count: toNumber(item.count) ?? 0,
      }));
  }, [analyticsData.hospitalStats?.monthly_trend]);

  const chdDistributionData = useMemo(() => {
    return (analyticsData.chdDistribution || []).map((entry, index) => ({
      name: entry.description || entry.code,
      value: toNumber(entry.count) ?? 0,
      percentage: toNumber(entry.percentage),
      color: COLORS[index % COLORS.length],
    }));
  }, [analyticsData.chdDistribution]);

  const severityData = useMemo(() => {
    return (analyticsData.severityStats || []).map((entry, index) => ({
      name: entry.level_name,
      value: toNumber(entry.count) ?? 0,
      percentage: toNumber(entry.percentage),
      color: COLORS[index % COLORS.length],
    }));
  }, [analyticsData.severityStats]);

  const geographicData = useMemo(() => {
    return (analyticsData.demographics?.geographic_distribution || [])
      .slice(0, 10)
      .map((entry) => ({
        judet: entry.judet,
        count: toNumber(entry.count) ?? 0,
      }));
  }, [analyticsData.demographics?.geographic_distribution]);

  const ageDistributionData = useMemo(() => {
    return (analyticsData.demographics?.age_distribution || []).map((entry) => ({
      age_group: entry.age_group,
      count: toNumber(entry.count) ?? 0,
    }));
  }, [analyticsData.demographics?.age_distribution]);

  const maternalAgeGroups = useMemo(() => {
    const maternal = analyticsData.maternalStats;
    if (!maternal) return [];
    const total = toNumber(maternal.total_with_maternal_data) ?? 0;
    const teen = toNumber(maternal.teen_mothers) ?? 0;
    const advanced = toNumber(maternal.advanced_age_mothers) ?? 0;
    const middle = Math.max(total - teen - advanced, 0);

    return [
      { age_group: '<20 ani', count: teen, percentage: total ? ((teen / total) * 100).toFixed(1) : '0.0' },
      { age_group: '20-34 ani', count: middle, percentage: total ? ((middle / total) * 100).toFixed(1) : '0.0' },
      { age_group: '≥35 ani', count: advanced, percentage: total ? ((advanced / total) * 100).toFixed(1) : '0.0' },
    ];
  }, [analyticsData.maternalStats]);

  const maternalRiskFactors = useMemo(() => {
    const maternal = analyticsData.maternalStats;
    if (!maternal) return [];
    return [
      { label: 'Fumat', value: toNumber(maternal.smoking_count) ?? 0 },
      { label: 'Alcool', value: toNumber(maternal.alcohol_count) ?? 0 },
      { label: 'Medicație teratogenă', value: toNumber(maternal.with_medications) ?? 0 },
      { label: 'Complicații sarcină', value: toNumber(maternal.with_complications) ?? 0 },
    ];
  }, [analyticsData.maternalStats]);

  const birthWeightCategories = useMemo(() => {
    return (analyticsData.birthWeight?.categories || []).map((entry) => ({
      weight_category: entry.weight_category,
      count: toNumber(entry.count) ?? 0,
      percentage: formatPercentage(entry.percentage),
    }));
  }, [analyticsData.birthWeight?.categories]);

  const birthWeightByGestationalAge = useMemo(() => {
    return (analyticsData.birthWeight?.by_gestational_age || []).map((entry) => ({
      gestational_age: toNumber(entry.gestational_age) ?? 0,
      avg_birth_weight: toNumber(entry.avg_birth_weight) ?? 0,
      count: toNumber(entry.count) ?? 0,
    }));
  }, [analyticsData.birthWeight?.by_gestational_age]);

  const totalPatients = toNumber(analyticsData.hospitalStats?.total_patients) ?? 0;
  const patientsWithDiagnoses = toNumber(analyticsData.hospitalStats?.patients_with_diagnoses) ?? 0;
  const totalDiagnoses = toNumber(analyticsData.hospitalStats?.total_diagnoses) ?? 0;
  const averageBirthWeight = toNumber(analyticsData.hospitalStats?.birth_weight_stats?.avg_birth_weight);
  const averageGestationalAge = toNumber(analyticsData.hospitalStats?.gestational_age_stats?.avg_gestational_age);

  const isAdminView = selectedHospital === 'all';

  const exportData = (formatType: 'pdf' | 'excel') => {
    // TODO: Wire up dedicated export endpoint if available
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((item) => (
              <Skeleton key={item} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analize sistem</h1>
            <p className="text-muted-foreground">
              Monitorizare statistică la nivel de registru MCC
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => exportData('pdf')}>
              <Download className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
            <Button onClick={() => exportData('excel')}>
              <Download className="mr-2 h-4 w-4" />
              Export Excel
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtre
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[220px]">
                <label className="text-sm font-medium mb-2 block">Perioada</label>
                <DatePickerWithRange date={dateRange} onDateChange={setDateRange} />
              </div>
              <div className="w-[220px]">
                <label className="text-sm font-medium mb-2 block">Spital</label>
                <Select value={selectedHospital} onValueChange={setSelectedHospital}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toate spitalele" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toate spitalele</SelectItem>
                    {hospitals.map((hospital) => (
                      <SelectItem key={hospital.id} value={hospital.id.toString()}>
                        {hospital.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total pacienți</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalPatients}</div>
              <p className="text-xs text-muted-foreground">
                {isAdminView ? 'La nivel național' : 'Pentru spitalul selectat'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pacienți cu diagnostice</CardTitle>
              <Heart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{patientsWithDiagnoses}</div>
              <p className="text-xs text-muted-foreground">
                {totalPatients ? `${((patientsWithDiagnoses / totalPatients) * 100).toFixed(1)}% din total` : ' '} 
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Diagnostice înregistrate</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalDiagnoses}</div>
              <p className="text-xs text-muted-foreground">
                Total cazuri MCC documentate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Indicatori neonatali</CardTitle>
              <Scale className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-sm">
                <div>
                  Greutate medie: <strong>{averageBirthWeight ? `${averageBirthWeight.toFixed(0)} g` : '-'}</strong>
                </div>
                <div>
                  Vârstă gestațională: <strong>{averageGestationalAge ? `${averageGestationalAge.toFixed(1)} săpt` : '-'}</strong>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Trend lunar înregistrări</span>
                <LineChartIcon className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {monthlyTrendData.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nu există date pentru perioada selectată.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={monthlyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" interval={monthlyTrendData.length > 6 ? 1 : 0} />
                    <YAxis />
                    <Tooltip />
                    <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Pacienți" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Distribuție diagnostice MCC</span>
                <PieChartIcon className="h-4 w-4 text-muted-foreground" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {chdDistributionData.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nu există diagnostice raportate în perioada selectată.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chdDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={85}
                      dataKey="value"
                      label={({ name, percentage }) => `${name}: ${formatPercentage(percentage)}`}
                    >
                      {chdDistributionData.map((entry, index) => (
                        <Cell key={`chd-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number, name: string, props: any) => [`${value} cazuri`, props.payload.name]} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Distribuție severitate</CardTitle>
            </CardHeader>
            <CardContent>
              {severityData.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nu există date despre severitate pentru perioada selectată.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={severityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value: number, name: string, props: any) => [`${value} cazuri`, props.payload.name]} />
                    <Bar dataKey="value">
                      {severityData.map((entry, index) => (
                        <Cell key={`severity-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Distribuție geografică (top 10)</CardTitle>
            </CardHeader>
            <CardContent>
              {geographicData.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nu există date geografice disponibile.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={geographicData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="judet" type="category" width={100} />
                    <Tooltip formatter={(value: number) => [`${value} cazuri`, 'Cazuri']} />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Distribuție vârstă la diagnostic</CardTitle>
          </CardHeader>
          <CardContent>
            {ageDistributionData.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nu există date privind vârsta la diagnostic.</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ageDistributionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="age_group" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => [`${value} cazuri`, 'Cazuri']} />
                  <Bar dataKey="count" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Greutate la naștere pe categorii</CardTitle>
            </CardHeader>
            <CardContent>
              {birthWeightCategories.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nu există date privind greutatea la naștere.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={birthWeightCategories}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="weight_category" interval={0} angle={-20} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip formatter={(value: number, name: string, props) => [`${value} cazuri`, props.payload.weight_category]} />
                    <Bar dataKey="count" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Greutate medie vs. vârstă gestațională</CardTitle>
            </CardHeader>
            <CardContent>
              {birthWeightByGestationalAge.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nu există date corelate pentru greutate și vârstă gestațională.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={birthWeightByGestationalAge}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="gestational_age" tickFormatter={(value) => `${value}s`} />
                    <YAxis tickFormatter={(value) => `${value}g`} />
                    <Tooltip
                      formatter={(value: unknown) => {
                        const numeric = typeof value === 'number' ? value : Number(value);
                        if (Number.isNaN(numeric)) {
                          return ['-', 'Greutate medie'];
                        }
                        return [`${numeric.toFixed(0)} g`, 'Greutate medie'];
                      }}
                    />
                    <Line type="monotone" dataKey="avg_birth_weight" stroke="#8b5cf6" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Grupe de vârstă maternă</CardTitle>
            </CardHeader>
            <CardContent>
              {maternalAgeGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nu există date materne pentru perioada selectată.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={maternalAgeGroups}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="age_group" />
                    <YAxis />
                    <Tooltip formatter={(value: number, name: string, props) => [`${value} cazuri`, props.payload.age_group]} />
                    <Bar dataKey="count" fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Factori de risc materni</CardTitle>
            </CardHeader>
            <CardContent>
              {maternalRiskFactors.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nu există factori de risc materni înregistrați.</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={maternalRiskFactors}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" interval={0} angle={-15} textAnchor="end" height={70} />
                    <YAxis />
                    <Tooltip formatter={(value: number, name: string, props) => [`${value} cazuri`, props.payload.label]} />
                    <Bar dataKey="value" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Map className="h-4 w-4 text-muted-foreground" />
              Distribuție regională
            </CardTitle>
          </CardHeader>
          <CardContent>
            {geographicData.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nu există date geografice disponibile pentru perioada selectată.
              </p>
            ) : (
              <ul className="space-y-2">
                {geographicData.map((entry) => (
                  <li key={entry.judet} className="flex items-center justify-between text-sm">
                    <span>{entry.judet}</span>
                    <span className="font-medium">{entry.count} cazuri</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};
